import type { AppointmentType } from '@/components/agenda/types';

export type AppointmentPayload = AppointmentType & { recurrenceRule?: string; allDay?: boolean };

export type SchedulerAppointmentTemplateData = {
    appointmentData?: AppointmentPayload;
    targetedAppointmentData?: AppointmentPayload;
};

/** Vista mes (Scheduler renovado): el modelo del template es un ítem preparado; los datos de negocio van en `rawAppointment`. */
function templatePayloadSource(input: unknown): AppointmentPayload | undefined {
    if (input == null || typeof input !== 'object') {
        return undefined;
    }
    const rec = input as Record<string, unknown>;
    const raw = rec.rawAppointment;
    if (raw != null && typeof raw === 'object') {
        return raw as AppointmentPayload;
    }
    return input as AppointmentPayload;
}

/** DevExtreme expone `appointmentData` y `targetedAppointmentData`; unifica sin que un `text` vacío pise el bueno. */
export function mergeAppointmentPayload(
    data: SchedulerAppointmentTemplateData | undefined,
): AppointmentPayload | null {
    if (!data?.appointmentData && !data?.targetedAppointmentData) {
        return null;
    }
    const a = templatePayloadSource(data.appointmentData);
    const t = templatePayloadSource(data.targetedAppointmentData);
    if (!a && !t) {
        return null;
    }
    const pickText = () => {
        const ta = a?.text != null ? String(a.text).trim() : '';
        if (ta.length > 0 && a) {
            return a.text;
        }
        const tt = t?.text != null ? String(t.text).trim() : '';
        if (tt.length > 0 && t) {
            return t.text;
        }
        return a?.text ?? t?.text ?? '';
    };
    return {
        ...t,
        ...a,
        text: pickText() as string,
    } as AppointmentPayload;
}

/** Línea 1: cliente | sitio | lugar (o `text` del dataSource). */
export function buildHeadline(apt: AppointmentType | AppointmentPayload): string {
    const fromText = apt.text != null ? String(apt.text).trim() : '';
    if (fromText.length > 0) {
        return fromText;
    }
    return [apt.parentClientName, apt.parentSiteName, apt.parentPlaceName]
        .map((n) => (n != null && String(n).trim() ? String(n).trim() : '--'))
        .join(' | ');
}

/** Línea 2 (agenda): postal/ciudad · tipo. */
export function buildSecondaryLine(apt: AppointmentType | AppointmentPayload, translate: (key: string) => string): string {
    const typeLabel = translate(`intervention.list.${apt.interventionTypeId}`);
    const postalCity = apt.postalCityLine?.trim() ?? '';
    return [postalCity, typeLabel].filter((s) => s.length > 0).join(' · ');
}

/** Vista mes: mismo texto que agenda (línea 1 + línea 2) en una sola cadena. */
export function buildAgendaStyleSingleLine(
    apt: AppointmentType | AppointmentPayload,
    translate: (key: string) => string,
): string {
    const headline = buildHeadline(apt);
    const secondary = buildSecondaryLine(apt, translate);
    return [headline, secondary].filter((p) => p.length > 0).join(' - ');
}
